import {
  initCoralogix,
  setCoralogixUserContext,
  setCoralogixLabels,
  logCoralogixError,
  logCoralogixInfo,
} from '@web-store-platforms/analytics';
import { webStorageUtil } from '@/state/webStorage.state.service';
import pkg from '../../package.json';
import { ELocalStorageKeys } from './enums';
import { PortalBootResponse } from './types';

const DEFAULT_CORALOGIX_APPLICATION = 'portal-fe';
const DEFAULT_CORALOGIX_PUBLIC_KEY_PROD = 'cxtp_TcaSLgpasuUkaEzk6BexF8nOwvv5LE';
const DEFAULT_CORALOGIX_SESSION_SAMPLE_RATE_PROD = 30;
const FULL_CORALOGIX_SESSION_SAMPLE_RATE = 100;
const isProdEnv = process.env.NODE_ENV === 'production';

/**
 * Same publisher-scoped flag the store reads. At the default 30% a publisher
 * asking about a named player is usually asking about a session that was never
 * recorded, which is what makes drop-off reports unanswerable.
 *
 * The sample decision is made once, inside initCoralogix, so the flag has to be
 * on the portal boot payload — it cannot come from the store boot query that
 * resolves later. Inert for now: game-portal-api, which serves portal/boot, has
 * no feature-flag client at all, so nothing ever sets this. Kept so the portal
 * starts honouring the flag the moment that payload grows one.
 */
const FULL_SESSION_SAMPLING_FLAG = 'store_rum_full_session_sampling';

/**
 * Until then, the publishers actively investigating widget -> web drop-offs are
 * listed here. By portal id rather than domain, so every domain a portal is
 * served on is covered by one entry.
 *
 * Only quiet sessions are at stake: a session that raises an error is recorded
 * whatever the rate, because the SDK is initialized with
 * alwaysTrackSessionsWithErrors and an error flushes the buffered session.
 */
const FULLY_SAMPLED_PORTAL_IDS = new Set([
  '6a394a768daf736277689953', // Candy Crush Portal/Hub — webhub.candycrushsaga.com
  '6a04730d4b285215ba1aaafc', // Candy Crush Portal/Hub — sandbox-webhub.candycrushsaga.com
]);

/** RUM-only names, kept out of the publisher/Mixpanel event vocabulary. */
export enum EPortalRumEvents {
  PORTAL_LANDED = 'PORTAL_LANDED',
  /** Every login failure, including the ones that never reach /failed. */
  PORTAL_LOGIN_FAILED = 'PORTAL_LOGIN_FAILED',
  /** Where a broken journey actually ends. */
  PORTAL_FAILED_PAGE_DISPLAYED = 'PORTAL_FAILED_PAGE_DISPLAYED',
  /** A logged-in player forced back out by a 401/403 mid-session. */
  PORTAL_SESSION_REJECTED = 'PORTAL_SESSION_REJECTED',
  PORTAL_LOGIN_CLICK_IGNORED = 'PORTAL_LOGIN_CLICK_IGNORED',
  PORTAL_LOADING_STALLED = 'PORTAL_LOADING_STALLED',
  PORTAL_LOADING_RECOVERED = 'PORTAL_LOADING_RECOVERED',
  PORTAL_LOADING_FAILED = 'PORTAL_LOADING_FAILED',
  PORTAL_JOURNEY_STARTED = 'PORTAL_JOURNEY_STARTED',
  PORTAL_JOURNEY_STEP_COMPLETED = 'PORTAL_JOURNEY_STEP_COMPLETED',
  PORTAL_JOURNEY_COMPLETED = 'PORTAL_JOURNEY_COMPLETED',
  PORTAL_JOURNEY_FAILED = 'PORTAL_JOURNEY_FAILED',
  PORTAL_JOURNEY_ABANDONED = 'PORTAL_JOURNEY_ABANDONED',
}

export interface IPortalPlayerRumContext {
  playerId: string;
  playerName?: string;
  playerCountry?: string;
  authMethod?: string;
  isTempSession?: boolean;
}

let portalLabels: Record<string, string> = {};

/**
 * The SDK drops any log raised before its init and only writes a console.debug,
 * and init runs from PortalApp while the events come from its children — React
 * runs child effects first, so the landing event was being dropped on every
 * load. Buffering makes the telemetry independent of that ordering.
 */
interface IBufferedRumEvent {
  isError: boolean;
  eventName: string;
  data: Record<string, unknown>;
}

// Only a handful can precede init; the cap just bounds a pathological loop.
const MAX_BUFFERED_EVENTS = 20;
const bufferedEvents: IBufferedRumEvent[] = [];
let bufferedPlayer: IPortalPlayerRumContext | undefined;
let isRumInitialized = false;

export const initCoralogixRum = (publisherMetaData: PortalBootResponse): void => {
  if (isProdEnv) {
    initCoralogix({
      publicKey: DEFAULT_CORALOGIX_PUBLIC_KEY_PROD,
      application: DEFAULT_CORALOGIX_APPLICATION,
      version: pkg.version,
      domain: 'US2',
      sessionSampleRate: resolveSessionSampleRate(publisherMetaData),
    });

    portalLabels = {
      publisherId: publisherMetaData.portalId,
      environment: 'production',
      version: pkg.version,
      storeName: publisherMetaData.portalName,
    };

    setCoralogixLabels(portalLabels);
    isRumInitialized = true;
    // Identity before the buffered events, which are stamped with whatever is
    // attached the moment they are released.
    applyPendingPlayerRumContext();
    flushBufferedRumEvents();
  }
};

const resolveSessionSampleRate = (portalBoot: PortalBootResponse): number =>
  portalBoot?.featureFlags?.[FULL_SESSION_SAMPLING_FLAG] ||
  FULLY_SAMPLED_PORTAL_IDS.has(portalBoot?.portalId ?? '')
    ? FULL_CORALOGIX_SESSION_SAMPLE_RATE
    : DEFAULT_CORALOGIX_SESSION_SAMPLE_RATE_PROD;

// A player known before init wins over storage: it came from a login in this
// document. Otherwise a returning player is restored from storage, since they
// are not logging in again and a refresh would carry no identity at all.
const applyPendingPlayerRumContext = (): void => {
  if (bufferedPlayer) {
    const player = bufferedPlayer;
    bufferedPlayer = undefined;
    setPortalPlayerRumContext(player);
    return;
  }

  const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
  if (playerData?.playerId) {
    setPortalPlayerRumContext({
      playerId: playerData.playerId,
      playerName: playerData.playerName,
      playerCountry: playerData.playerCountry,
      authMethod: playerData.authMethod,
    });
  }
};

const flushBufferedRumEvents = (): void => {
  bufferedEvents.splice(0).forEach(({ isError, eventName, data }) => {
    if (isError) {
      logCoralogixError(eventName, data);
      return;
    }
    logCoralogixInfo(eventName, data);
  });
};

const bufferRumEvent = (event: IBufferedRumEvent): void => {
  if (bufferedEvents.length === MAX_BUFFERED_EVENTS) {
    bufferedEvents.shift();
  }
  bufferedEvents.push(event);
};

// The RUM identity has to be the player, not the portal — a portal-wide
// user_id makes every session look identical and leaves publisher-reported
// player IDs unsearchable.
export const setPortalPlayerRumContext = (player: IPortalPlayerRumContext): void => {
  if (!isProdEnv) return;

  if (!isRumInitialized) {
    bufferedPlayer = player;
    return;
  }

  setCoralogixUserContext({
    userId: player.playerId,
    userName: player.playerName ?? '',
    metadata: portalLabels,
  });

  setCoralogixLabels({
    ...portalLabels,
    playerId: player.playerId,
    playerCountry: player.playerCountry ?? '',
    authMethod: player.authMethod ?? '',
    isTempSession: String(player.isTempSession ?? false),
  });
};

export const logPortalRumInfo = (eventName: string, data: Record<string, unknown>): void => {
  if (!isProdEnv) return;

  if (!isRumInitialized) {
    bufferRumEvent({ isError: false, eventName, data });
    return;
  }
  logCoralogixInfo(eventName, data);
};

// Error level so a failure is still recorded in sessions the sampler dropped —
// the failures are the sessions publishers ask about.
export const logPortalRumError = (eventName: string, data: Record<string, unknown>): void => {
  if (!isProdEnv) return;

  if (!isRumInitialized) {
    bufferRumEvent({ isError: true, eventName, data });
    return;
  }
  logCoralogixError(eventName, data);
};
